import apiClient from './axios';

/**
 * Get all products with pagination, search, and filter options
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.perPage - Items per page (default: 10)
 * @param {string} params.search - Search term (default: '')
 * @param {string} params.category - Filter by category
 * @param {string} params.sorting - Sort order (asc/desc, default: 'asc')
 * @param {string} params.sort - Sort field
 * @param {string} params.order - Sort order (asc/desc)
 * @returns {Promise} - Promise with products data
 */
export const getAllProducts = async (params = {}) => {
  try {
    // Prepare query parameters with proper defaults
    const queryParams = {
      page: params.page || 1,
      perPage: params.perPage || 10,
      sorting: params.sorting || 'asc'
    };

    // Add optional parameters only if they exist
    if (params.search) queryParams.search = params.search;
    if (params.category) queryParams.category = params.category;
    if (params.type) queryParams.type = params.type;
    if (params.sort) queryParams.sort = params.sort;
    if (params.order) queryParams.order = params.order;

    const response = await apiClient.get('/api/admin/get-all-products', { params: queryParams });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get products by specific category
 * @param {string} categoryName - Category name to filter by
 * @param {Object} params - Additional query parameters
 * @returns {Promise} - Promise with category-specific products data
 */
export const getProductsByCategory = async (categoryName, params = {}) => {
  try {
    const queryParams = {
      page: params.page || 1,
      perPage: params.perPage || 10,
      sorting: params.sorting || 'asc',
      category: categoryName
    };

    // Add optional parameters
    if (params.search) queryParams.search = params.search;
    if (params.type) queryParams.type = params.type;
    if (params.sort) queryParams.sort = params.sort;
    if (params.order) queryParams.order = params.order;

    const response = await apiClient.get('/api/admin/get-all-products', { params: queryParams });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get product categories
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.perPage - Items per page (default: 10)
 * @param {string} params.search - Search term (default: '')
 * @param {string} params.sorting - Sort order (default: 'asc')
 * @returns {Promise} - Promise with categories data
 */
export const getProductCategories = async (params = {}) => {
  try {
    const queryParams = {
      page: params.page || 1,
      perPage: params.perPage || 10,
      search: params.search || '',
      sorting: params.sorting || 'asc'
    };
    const response = await apiClient.get('/api/admin/get-all-category', { params: queryParams });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get category by ID
 * @param {string} categoryId - Category ID
 * @returns {Promise} - Promise with category data
 */
export const getCategoryById = async (categoryId) => {
  try {
    // Try different possible endpoints
    let response;
    try {
      response = await apiClient.get(`/api/admin/get-all-category/${categoryId}`);
    } catch (firstError) {
      try {
        // Try alternative endpoint structure
        response = await apiClient.get(`/api/admin/category/${categoryId}`);
      } catch (secondError) {
        // Try third option - get all categories and filter
        const allCategoriesResponse = await apiClient.get('/api/admin/get-all-category');
        const allCategories = allCategoriesResponse.data.categories || allCategoriesResponse.data.data || allCategoriesResponse.data;
        const foundCategory = allCategories.find(cat => 
          cat._id === categoryId || cat.id === categoryId || cat.category_id === categoryId
        );
        if (foundCategory) {
          response = { data: foundCategory };
        } else {
          throw new Error('Category not found in any endpoint');
        }
      }
    }
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get product with full category details by product ID
 * @param {string} id - Product ID
 * @returns {Promise} - Promise with product and category data
 */
export const getProductWithCategory = async (id) => {
  try {
    
    // First get the product
    const productData = await getProductById(id);
    let product = productData.product || productData.data || productData;
    
    // If product has category_id, fetch category details
    if (product.category_id || product.categoryId) {
      try {
        const categoryData = await getCategoryById(product.category_id || product.categoryId);
        const category = categoryData.category || categoryData.data || categoryData;
        
        // Merge category information into product
        product = {
          ...product,
          category_details: category,
          category_name: category.category_name || category.name || category.title,
          category: category.category_name || category.name || category.title
        };
        
      } catch (categoryError) {
        // Continue with product data even if category fetch fails
      }
    }
    
    return { product };
  } catch (error) {
    throw error;
  }
};

/**
 * Get products by category ID - fetches product collection for a specific category
 * @param {string} categoryId - Category ID to fetch products for
 * @param {Object} params - Additional query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.perPage - Items per page (default: 10)
 * @param {string} params.search - Search term (default: '')
 * @param {string} params.sorting - Sort order (default: 'asc')
 * @param {string} params.sort - Sort field
 * @param {string} params.order - Sort order (asc/desc)
 * @param {string} params.type - Filter by product type
 * @returns {Promise} - Promise with category products data
 */
export const getProductsByCategoryId = async (categoryId, params = {}) => {
  try {
    
    // Prepare query parameters with proper defaults
    const queryParams = {
      page: params.page || 1,
      perPage: params.perPage || 10,
      sorting: params.sorting || 'asc'
    };

    // Add optional parameters only if they exist
    if (params.search) queryParams.search = params.search;
    if (params.type) queryParams.type = params.type;
    if (params.sort) queryParams.sort = params.sort;
    if (params.order) queryParams.order = params.order;

    const response = await apiClient.get(`/api/admin/get-category/${categoryId}`, { params: queryParams });
    
    const apiPayload = response.data;
    const payloadData = apiPayload?.data || apiPayload?.category || apiPayload;
    const paginationMeta = payloadData?.pagination || apiPayload?.pagination || {};

    const toFiniteNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : undefined;
    };

    const resolvedPerPage =
      toFiniteNumber(paginationMeta?.perPage) ??
      toFiniteNumber(payloadData?.perPage) ??
      toFiniteNumber(apiPayload?.perPage) ??
      toFiniteNumber(queryParams.perPage) ??
      10;

    const resolvedCurrentPage =
      toFiniteNumber(paginationMeta?.currentPage) ??
      toFiniteNumber(payloadData?.page) ??
      toFiniteNumber(apiPayload?.page) ??
      toFiniteNumber(queryParams.page) ??
      1;

    let resolvedProducts = [];
    if (Array.isArray(payloadData?.products)) {
      resolvedProducts = payloadData.products;
    } else if (Array.isArray(payloadData?.product)) {
      resolvedProducts = payloadData.product;
    } else if (Array.isArray(payloadData?.data)) {
      resolvedProducts = payloadData.data;
    } else if (Array.isArray(apiPayload?.products)) {
      resolvedProducts = apiPayload.products;
    } else if (Array.isArray(apiPayload?.data?.product)) {
      resolvedProducts = apiPayload.data.product;
    } else if (Array.isArray(apiPayload?.data)) {
      resolvedProducts = apiPayload.data;
    }

    if (Array.isArray(resolvedProducts) && resolvedProducts.length > 0 && resolvedProducts[0]?.product) {
      resolvedProducts = resolvedProducts.map((item) => {
        const productDetails = item.product || {};
        return {
          ...productDetails,
          category: item.category_name || productDetails.category || '',
          category_name: item.category_name || productDetails.category_name || '',
          categoryId: item._id || productDetails.categoryId || categoryId
        };
      });
    }

    const inferredTotal =
      toFiniteNumber(paginationMeta?.total) ??
      toFiniteNumber(payloadData?.total) ??
      toFiniteNumber(payloadData?.totalProducts) ??
      toFiniteNumber(payloadData?.totalItems) ??
      toFiniteNumber(apiPayload?.total) ??
      toFiniteNumber(apiPayload?.count) ??
      (Array.isArray(resolvedProducts) ? resolvedProducts.length : 0);

    const resolvedTotalPages =
      toFiniteNumber(paginationMeta?.totalPages) ??
      toFiniteNumber(payloadData?.totalPages) ??
      toFiniteNumber(apiPayload?.totalPages) ??
      toFiniteNumber(apiPayload?.pages) ??
      (resolvedPerPage ? Math.ceil((inferredTotal || 0) / resolvedPerPage) : 1);

    return {
      status: apiPayload?.status,
      message: apiPayload?.message,
      products: resolvedProducts,
      totalItems: inferredTotal,
      totalPages: Math.max(1, resolvedTotalPages || 1),
      perPage: resolvedPerPage,
      currentPage: resolvedCurrentPage,
      category: {
        id: payloadData?._id || payloadData?.id || payloadData?.category_id || categoryId,
        name: payloadData?.category_name || payloadData?.categoryName || payloadData?.name || payloadData?.title || ''
      }
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get product by ID
 * @param {string} id - Product ID
 * @returns {Promise} - Promise with product data
 */
export const getProductById = async (id) => {
  try {
    const response = await apiClient.get(`/api/admin/get-product/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};
